import Rellax from "rellax";
import { isBelowBreakpoint } from "../helpers/responsiveCheck.js";
import { onReducedMotionChange, prefersReducedMotion } from "../helpers/reducedMotion.js";

// Parallax belongs to the landscape composition, where the imagery is a full-bleed
// backdrop and a translated photo never exposes its own box edge. In the portrait
// composition (_hero.scss) the imagery is an in-flow content block sized exactly to
// its grid track, so the same translate vacates a strip of the track — rellax-desktop
// elements are therefore excluded on portrait screens just as they are below the
// breakpoint. The orientation query is the same platform primitive the `portrait` /
// `landscape` mixins use; unlike the 768px boundary there is no project-defined
// threshold to drift from CSS, so matchMedia is safe here where a hardcoded width
// would not be (see the #breakpoints sentinel in ARCHITECTURE.md).
const portraitQuery = window.matchMedia("(orientation: portrait)");

const rellaxSelector = () => (isBelowBreakpoint() || portraitQuery.matches ? ".rellax:not(.rellax-desktop)" : ".rellax");

// Rellax writes a transform on every scroll frame and offers no way to be told to hold
// still, so honoring the preference means never constructing it. destroy() restores each
// element's original cssText and clears the loop, which is what makes this safe to run
// in both directions instead of one-way at load. No default export any more: nothing
// imports the instance, and a binding that is sometimes null would only invite it.
let rellax = null;

const applyMotionPreference = () => {
    if (prefersReducedMotion()) {
        rellax?.destroy();
        rellax = null;
        return;
    }

    if (rellax) return;

    const selector = rellaxSelector();

    // Rellax's constructor returns early — before it has defined destroy() — when the
    // selector matches nothing, which is every portrait or phone load (all parallax
    // elements are .rellax-desktop). An instance without destroy() cannot be torn down
    // on the next orientation or motion-preference flip, so never hold one: construct
    // only when there is something to drive.
    if (document.querySelector(selector)) rellax = new Rellax(selector);
};

applyMotionPreference();
onReducedMotionChange(applyMotionPreference);

// A rotation changes which set should be live, and Rellax cannot re-select — rebuild.
// destroy() restores each element's inline style, so the excluded set comes back clean.
portraitQuery.addEventListener("change", () => {
    rellax?.destroy();
    rellax = null;
    applyMotionPreference();
});
